/**
 * "When this document was generated, these fields were blank" (VALE-788).
 *
 * Generation has always known which optional merge fields came back empty:
 * `GenerationService` runs its readiness check inside every render and hands
 * the list to each of its consumers. What none of them did was keep it. Three
 * discarded it outright and the fourth named it in a toast, so a lawyer who
 * generated a document on Monday and sent it on Wednesday had no way, on
 * Wednesday, to learn its address line was empty. A blank inside a sentence is
 * far harder to spot than a missing one, which is why silence was the defect
 * rather than the blank itself.
 *
 * ## The wording is load-bearing, not decoration
 *
 * The stored list describes **the render**, not the matter as it stands now. A
 * lawyer who fills the address in without regenerating still sees it. Read-time
 * recomputation and write-time invalidation were both considered and rejected:
 * they buy accuracy that honest past-tense wording already provides, at the cost
 * of machinery on four surfaces. So this never says "these fields are missing",
 * and the action it offers is the Regenerate control the surface already has.
 *
 * ## Advisory, never a gate
 *
 * Every field this can name is classified optional, so the document generated
 * and will send. `tone="caution"` and no blocking behaviour, matching the
 * firm-precedent drawer's own warn-without-blocking pattern.
 *
 * ## Short, and only as specific as Vale actually is
 *
 * The title names what is blank; a place to fix it is said only where Vale knows one, and the
 * surface's own Regenerate button carries the next step. The earlier version spent four sentences
 * on every render ("It is still fine to send…", "Recording it does not change…") and pointed
 * unknown fields at a generic instruction, which was a lot of text that told the reader nothing
 * they could act on.
 *
 * ## The remedy is stated per remedy, not per field
 *
 * The fields are grouped by where they are fixed, and the fix is written once
 * per group. A row each was the first shape, and on the common case — several
 * fields that are all fixed in the same place — it rendered three bordered
 * cards inside a bordered banner, each repeating the same sentence, to convey
 * one fact and three names.
 */

import { Banner } from '@/components/ui/banner';
import type { BannerAction } from '@/components/ui/banner';
import {
  getMergeFieldLabel,
  getMergeFieldFixHint,
  hasMergeFieldFixLocation
} from '@/lib/services/precedents/merge-field-labels';

/** "A", "A and B", "A, B and C" — the list separator prose uses, not a comma-join. */
function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function MissingFieldsBanner({
  fields,
  /**
   * What the document is: "letter", "declaration". Read into the one supporting line so each
   * surface names its own artefact.
   */
  documentNoun = 'document',
  /**
   * The surface's existing Regenerate / re-draft control. It carries the "fix it, then
   * regenerate" step, so the banner does not also have to say it in a sentence.
   */
  action,
  className
}: {
  fields: string[] | null | undefined;
  documentNoun?: string;
  action?: BannerAction;
  className?: string;
}) {
  // Absent and empty read identically on purpose. `null` is a row generated
  // before the list was recorded, which is unknown rather than incomplete, and
  // inventing a warning for it would put a caution on every historical
  // document in the product on the day this shipped.
  if (!fields || fields.length === 0) return null;

  const labels = fields.map(getMergeFieldLabel);

  // Only the places Vale actually knows, grouped so each is said once. A field with no known
  // home is named in the title and nothing else: a generic "add the missing detail" gave the
  // reader an instruction with nowhere to carry it out.
  const byHint = new Map<string, string[]>();
  for (const tag of fields) {
    if (!hasMergeFieldFixLocation(tag)) continue;
    const hint = getMergeFieldFixHint(tag);
    byHint.set(hint, [...(byHint.get(hint) ?? []), getMergeFieldLabel(tag)]);
  }
  const groups = Array.from(byHint.entries());
  /*
    When one place fixes everything, the `line` above has already named every
    field, so naming them again in front of the remedy says the same list twice
    in three lines. The common case is exactly this: three merge fields that all
    live on the client's own record.
  */
  const oneHomeForEverything = groups.length === 1 && groups[0][1].length === fields.length;

  return (
    <Banner
      tone="caution"
      title={
        fields.length === 1
          ? `${labels[0]} is blank in this ${documentNoun}`
          : `${fields.length} details are blank in this ${documentNoun}`
      }
      line={fields.length === 1 ? undefined : `${joinWithAnd(labels)}.`}
      action={action}
      className={className}
    >
      {groups.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {groups.map(([hint, groupLabels]) => (
            <li key={hint} className="text-muted-foreground text-12px">
              {fields.length === 1 || oneHomeForEverything
                ? hint
                : `${joinWithAnd(groupLabels)}: ${hint.charAt(0).toLowerCase()}${hint.slice(1)}`}
              .
            </li>
          ))}
        </ul>
      )}
    </Banner>
  );
}
